import { HUText } from "@modules/ui/components/huText"
import { classNames } from "primereact/utils"
import { useTheme } from "styled-components"

export type InputContainerProps = {
  children: React.ReactNode
  isInvalid: boolean
  label?: string
  isRequiredInput?: boolean
  name?: string
  error?: React.JSX.Element
  className?: string
} & React.HTMLAttributes<HTMLDivElement>

/**
 * InputContainer component is a wrapper for form elements with additional features such as labels, error messages, and required indicators.
 *
 * @component
 * @param {Object} props - The properties object.
 * @param {React.ReactNode} props.children - The form elements to be wrapped by the container.
 * @param {string} [props.label] - The label text for the form element.
 * @param {boolean} [props.isRequiredInput] - Indicates if the form element is required.
 * @param {boolean} props.isInvalid - Indicates if the form element is in an invalid state.
 * @param {boolean} [props.labelDisabled] - Indicates if the label should be displayed in a disabled state.
 * @param {string} [props.name] - The name attribute for the form element, used for the `htmlFor` attribute of the label.
 * @param {React.JSX.Element} [props.error] - The error message element to be displayed when the form element is invalid.
 * @param {React.HTMLAttributes<HTMLDivElement>} props - Additional HTML attributes to be applied to the container div.
 *
 * @returns {React.JSX.Element} The rendered InputContainer component.
 */
export const InputContainer: React.FC<InputContainerProps> = ({
  children,
  label,
  name,
  isRequiredInput,
  isInvalid,
  error,
  className,
  ...props
}) => {
  const theme = useTheme()
  const defaultClassName = "flex flex-column w-full"
  return (
    <div className={className ?? defaultClassName} {...props}>
      {label && (
        <HUText
          type="label"
          color={theme.inputLabelColor}
          fontStyle="LS"
          className={classNames({ "p-error": isInvalid })}
          textProps={{ htmlFor: name }}
        >
          {label.toUpperCase()}
          {isRequiredInput && (
            <HUText fontStyle="LL" color={theme.necessaryInput}>
              {" "}
              *
            </HUText>
          )}
        </HUText>
      )}
      {children}
      {error}
    </div>
  )
}
